import ChevronRight from '@ixigo/icons/ChevronRight';
import Edit from '@ixigo/icons/EditFilled';
import Collapse from '@ixigo/iui/Collapse';
import * as Timeline from '@ixigo/iui/Timeline';
import { useState } from 'react';
import type { RideDetails } from '@/api';
import { useTimelineItems } from './hooks';
import { LocationLabel } from './location-label';
import { LocationSubtext } from './location-subtext';
import { RideTimelineItem } from './ride-timeline-item';
import { TimelineIndicator } from './timeline-icons';

export interface WaypointTimelineProps {
	rideDetails: Omit<RideDetails, 'searchSessionId'>;
	/** Mode of the trip details card */
	/**
	 * Edit: Edit mode, where the user can edit the location
	 * View: View mode, where the user can view the location
	 * Collapsible: Collapsible mode, where the user can collapse the location
	 * @default 'View'
	 */
	mode?: 'View' | 'Edit' | 'Collapsible';
	onEditLocation?: (locationType: 'pickup' | 'drop') => void;
	expanded?: boolean;
}

export const WaypointTimeline = ({
	rideDetails,
	mode,
	onEditLocation,
	expanded = true,
}: WaypointTimelineProps) => {
	const [isExpanded, setIsExpanded] = useState(
		mode !== 'Collapsible' || expanded,
	);
	const handleEditPickup = () => {
		onEditLocation?.('pickup');
	};

	const handleEditDropoff = () => {
		onEditLocation?.('drop');
	};

	const {
		timelineItems,
		alwaysShowTimelineItem: firstTimelineItem,
		otherTimelineItems,
	} = useTimelineItems(rideDetails);
	return (
		<Timeline.Root
			orientation="vertical"
			contentPosition="end"
			className="w-full overflow-hidden"
		>
			{firstTimelineItem && (
				<Timeline.Item key={firstTimelineItem.id} className="relative">
					<Timeline.Separator
						indicator={<TimelineIndicator item={firstTimelineItem} />}
						connector={
							<div
								className={`w-px h-full border-l border-secondary ${timelineItems.length === 1 || (!isExpanded && 'hidden')}`}
							/>
						}
						className="!w-20"
					/>
					{firstTimelineItem.type !== 'cab' && (
						<Timeline.Content className="overflow-hidden">
							<div className="pb-2.5">
								<LocationLabel
									label={firstTimelineItem.location?.label || ''}
									className="truncate relative"
									{...(mode === 'Edit' &&
										onEditLocation && {
											actionIcon: <Edit className="w-6 h-6 shrink-0" />,
											actionHandler:
												firstTimelineItem.type === 'pickup'
													? handleEditPickup
													: handleEditDropoff,
										})}
									{...(mode === 'Collapsible' && {
										actionIcon: (
											<ChevronRight
												className={`transition-transform duration-300 ${isExpanded ? '-rotate-90' : 'rotate-90'}`}
											/>
										),
										actionHandler: () => {
											setIsExpanded(!isExpanded);
										},
									})}
								/>
								<LocationSubtext
									label={firstTimelineItem.location?.subText}
									time={firstTimelineItem.location?.time}
								/>
							</div>
						</Timeline.Content>
					)}
				</Timeline.Item>
			)}
			{otherTimelineItems.length > 0 && (
				<Collapse in={isExpanded}>
					{otherTimelineItems.map((item, index) => (
						<RideTimelineItem
							key={item.id}
							item={item}
							isLast={index === otherTimelineItems.length - 1}
						/>
					))}
				</Collapse>
			)}
		</Timeline.Root>
	);
};
